(() => {
  const directory = document.querySelector('[data-dyberry-brands]');
  if (!directory) return;

  const MANIFEST_CACHE_KEY = 'dyberry-brands-manifest-v1';
  const searchUrl = directory.dataset.searchUrl;
  const status = directory.querySelector('[data-brands-status]');
  const output = directory.querySelector('[data-brands-directory]');
  const filters = [...directory.querySelectorAll('[data-brands-filter]')];
  const totals = {
    all: directory.querySelector('[data-brands-total="all"]'),
    dude: directory.querySelector('[data-brands-total="dude"]'),
    lady: directory.querySelector('[data-brands-total="lady"]'),
    dino: directory.querySelector('[data-brands-total="dino"]'),
  };
  const labels = { all: 'All brands', dude: 'Dude', lady: 'Lady', dino: 'Dino' };
  const iconMarkup = {
    dude: 'D',
    lady: 'L',
    dino: '<svg viewBox="0 0 32 32" focusable="false" aria-hidden="true"><path d="M5 21c0-6 4-11 11-11 2 0 3 1 4 2l4-4 3 3-4 4c1 2 2 4 2 6 0 4-3 7-8 7h-1v-4h-4v4H9v-3H5v-4Zm7-4a1.6 1.6 0 1 0 0 .1Zm9 2h2v2h-2z" fill="currentColor"/></svg>',
  };
  let activeAudience = 'all';
  let brands = [];

  const number = (value) => Math.max(0, Number.parseInt(value, 10) || 0);
  const validManifest = (candidate) => candidate && candidate.schema_version === 1 && candidate.totals && Array.isArray(candidate.brands);

  const cachedManifest = () => {
    try {
      const value = window.localStorage.getItem(MANIFEST_CACHE_KEY);
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  };

  const rememberManifest = (manifest) => {
    try {
      window.localStorage.setItem(MANIFEST_CACHE_KEY, JSON.stringify(manifest));
    } catch (error) {
      // Storage can be unavailable in private browsing; the live manifest still renders.
    }
  };

  const catalogLink = (brand, audience) => {
    const vendor = brand.searchVendor || brand.vendor;
    const escapedVendor = String(vendor).replace(/([\\"])/g, '\\$1');
    const clauses = [`vendor:"${escapedVendor}"`];
    if (audience === 'dude') clauses.push('tag:Men');
    if (audience === 'lady') clauses.push('tag:Women');
    if (audience === 'dino') clauses.push('NOT tag:Men', 'NOT tag:Women');

    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', clauses.join(' AND '));
    return url.toString();
  };

  const updateFilterState = () => {
    filters.forEach((filter) => {
      const selected = filter.dataset.brandsFilter === activeAudience;
      filter.classList.toggle('is-active', selected);
      filter.setAttribute('aria-pressed', String(selected));
    });
  };

  const chip = (brand, audience, count) => {
    const token = document.createElement('a');
    token.className = `dyberry-brands__chip dyberry-brands__chip--${audience}`;
    token.href = catalogLink(brand, audience);
    token.setAttribute('aria-label', `${brand.vendor}: ${count} ${labels[audience]} item${count === 1 ? '' : 's'}`);

    const icon = document.createElement('span');
    icon.className = 'dyberry-brands__chip-icon';
    if (audience === 'dino') icon.innerHTML = iconMarkup.dino;
    else icon.textContent = iconMarkup[audience];
    const text = document.createElement('span');
    text.textContent = `${labels[audience]} ${count}`;
    token.append(icon, text);
    return token;
  };

  const render = () => {
    const visible = brands.filter((brand) => activeAudience === 'all' || brand.counts[activeAudience] > 0);
    output.replaceChildren();

    if (!visible.length) {
      const empty = document.createElement('p');
      empty.className = 'dyberry-brands__empty';
      empty.textContent = 'No brands are available in this group right now.';
      output.append(empty);
      return;
    }

    const groups = new Map();
    visible.forEach((brand) => {
      const first = brand.vendor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(first) ? first : '#';
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter).push(brand);
    });

    [...groups.keys()].sort((a, b) => a.localeCompare(b)).forEach((letter) => {
      const group = document.createElement('section');
      group.className = 'dyberry-brands__letter-group';
      const heading = document.createElement('h2');
      heading.className = 'dyberry-brands__letter';
      heading.textContent = letter;
      const list = document.createElement('ul');
      list.className = 'dyberry-brands__brand-list';

      groups.get(letter).forEach((brand) => {
        const row = document.createElement('li');
        row.className = 'dyberry-brands__brand-row';
        const name = document.createElement('a');
        name.className = 'dyberry-brands__brand-link';
        name.href = catalogLink(brand, 'all');
        name.textContent = brand.vendor;
        const chips = document.createElement('div');
        chips.className = 'dyberry-brands__chips';
        ['dude', 'lady', 'dino'].forEach((audience) => {
          if (brand.counts[audience] > 0) chips.append(chip(brand, audience, brand.counts[audience]));
        });
        row.append(name, chips);
        list.append(row);
      });
      group.append(heading, list);
      output.append(group);
    });

    status.textContent = `${visible.length} ${activeAudience === 'all' ? 'brands' : `${labels[activeAudience]} brands`} available.`;
  };

  const useManifest = (manifest, isCached) => {
    brands = manifest.brands
      .map((brand) => ({
        vendor: String(brand.vendor || '').trim(),
        searchVendor: String(brand.search_vendor || brand.vendor || '').trim(),
        counts: {
          all: number(brand.counts && brand.counts.all),
          dude: number(brand.counts && brand.counts.dude),
          lady: number(brand.counts && brand.counts.lady),
          dino: number(brand.counts && brand.counts.dino),
        },
      }))
      .filter((brand) => brand.vendor && brand.counts.all > 0)
      .sort((left, right) => left.vendor.localeCompare(right.vendor));

    const manifestTotals = {
      items: number(manifest.totals.items),
      brands: number(manifest.totals.brands) || brands.length,
      dude: number(manifest.totals.dude),
      lady: number(manifest.totals.lady),
      dino: number(manifest.totals.dino),
    };
    totals.all.textContent = manifestTotals.brands;
    ['dude', 'lady', 'dino'].forEach((audience) => { totals[audience].textContent = manifestTotals[audience]; });
    status.textContent = `${brands.length} brands · ${manifestTotals.items} pieces in the current inventory.${isCached ? ' Showing a recent saved directory.' : ''}`;
    render();
  };

  filters.forEach((filter) => {
    filter.addEventListener('click', () => {
      activeAudience = filter.dataset.brandsFilter;
      updateFilterState();
      render();
    });
  });

  const liveManifest = window.DYBERRY_BRANDS_MANIFEST;
  if (validManifest(liveManifest)) {
    rememberManifest(liveManifest);
    useManifest(liveManifest, false);
    return;
  }

  const savedManifest = cachedManifest();
  if (validManifest(savedManifest)) {
    useManifest(savedManifest, true);
    return;
  }

  status.textContent = 'The brand directory is temporarily unavailable. Please refresh and try again.';
})();
